/**
 * Contestant Utility Functions
 * 
 * Provides helper functions for converting between contestant IDs and names.
 * Handles backward compatibility with both ID and name formats.
 */

/**
 * Get contestant ID from value (handles both ID and name formats)
 * @param {string} value - Either a contestantId or contestantName
 * @param {Array} contestants - Array of contestant objects with id and name
 * @returns {string|null} - Contestant ID or null if not found
 */
export function normalizeToContestantId(value, contestants) {
  if (!value || !contestants || contestants.length === 0) return null;
  
  // If it's already an ID (matches a contestant.id), return it
  const byId = contestants.find(c => c.id === value);
  if (byId) return byId.id;
  
  // If it's a name, find the ID
  const byName = contestants.find(c => c.name === value);
  return byName ? byName.id : null;
}

/**
 * Get contestant name from value (handles both ID and name formats)
 * @param {string} value - Either a contestantId or contestantName
 * @param {Array} contestants - Array of contestant objects with id and name
 * @returns {string} - Contestant name or the original value if not found
 */
export function normalizeToContestantName(value, contestants) {
  if (!value) return 'N/A';
  if (!contestants || contestants.length === 0) return value;
  
  // If it's a name, return it
  const byName = contestants.find(c => c.name === value);
  if (byName) return byName.name;
  
  // If it's an ID, find the name
  const byId = contestants.find(c => c.id === value);
  return byId ? byId.name : value; // Fallback to original value
}

/**
 * Get contestant by ID
 * @param {string} contestantId - Contestant ID
 * @param {Array} contestants - Array of contestant objects with id and name
 * @returns {Object|null} - Contestant object or null if not found
 */
export function getContestantById(contestantId, contestants) {
  if (!contestantId || !contestants || contestants.length === 0) return null;
  return contestants.find(c => c.id === contestantId) || null;
}

/**
 * Get contestant by name
 * @param {string} name - Contestant name
 * @param {Array} contestants - Array of contestant objects with id and name
 * @returns {Object|null} - Contestant object or null if not found
 */
export function getContestantByName(name, contestants) {
  if (!name || !contestants || contestants.length === 0) return null;
  return contestants.find(c => c.name === name) || null;
}

/**
 * Normalize `predictions.votedOut` to an ordered list of contestant IDs.
 * Handles single elimination (string ID), arrays of IDs, and double/triple
 * elimination slots stored as `{ contestantId, position }` objects.
 *
 * @param {string|Array<string|{contestantId?: string, position?: number}>} votedOut
 * @returns {Array<string>}
 */
export function normalizeVotedOutToOrderedContestantIds(votedOut) {
  if (votedOut == null) return [];
  if (typeof votedOut === 'string') {
    return votedOut ? [votedOut] : [];
  }
  if (!Array.isArray(votedOut) || votedOut.length === 0) return [];

  const first = votedOut[0];
  const isObjectSlot =
    first &&
    typeof first === 'object' &&
    !Array.isArray(first) &&
    ('contestantId' in first || 'position' in first);

  if (isObjectSlot) {
    return [...votedOut]
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map((item) =>
        item && item.contestantId != null ? item.contestantId : null
      )
      .filter(Boolean);
  }

  return votedOut.filter(Boolean);
}

/**
 * Get multiple contestant names from IDs
 * @param {Array<string>} contestantIds - Array of contestant IDs
 * @param {Array} contestants - Array of contestant objects with id and name
 * @returns {Array<string>} - Array of contestant names
 */
export function getContestantNamesFromIds(contestantIds, contestants) {
  if (!contestantIds || contestantIds.length === 0) return [];
  if (!contestants || contestants.length === 0) return contestantIds;
  
  return contestantIds
    .map(id => {
      const contestant = getContestantById(id, contestants);
      return contestant ? contestant.name : id;
    })
    .filter(Boolean);
}

/**
 * Get multiple contestant IDs from names
 * @param {Array<string>} names - Array of contestant names
 * @param {Array} contestants - Array of contestant objects with id and name
 * @returns {Array<string>} - Array of contestant IDs
 */
export function getContestantIdsFromNames(names, contestants) {
  if (!names || names.length === 0) return [];
  if (!contestants || contestants.length === 0) return [];
  
  return names
    .map(name => {
      const contestant = getContestantByName(name, contestants);
      return contestant ? contestant.id : null;
    })
    .filter(Boolean);
}

